import './Palette.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type WindowID, resolvedPosition, useWindows } from './Windows';
import {
  Column,
  Font,
  PaletteBorderStyle,
  Row,
  Scrollable,
} from './DesignSystem';
import { Spacer } from './ColorPreview';

export type PaletteProps = {
  title: string;
  windowID: WindowID;
  headless?: boolean;
  leadingAction?: React.ReactNode;
  trailingAction?: React.ReactNode;
  footerAction?: React.ReactNode;
};

export const PaletteMetrics = {
  headerHeight: 52,
  backgroundColor: '#ffffff',
  padding: 20,
};
export function Palette({
  title,
  windowID,
  headless,
  leadingAction,
  trailingAction,
  children,
  footerAction,
}: React.PropsWithChildren<PaletteProps>) {
  const { state: windowState } = useWindows();
  const maxHeight = windowState.windows[windowID]?.size?.maxHeight;
  return (
    <Draggable windowID={windowID}>
      <Column
        className="blurry"
        style={{
          overflow: 'hidden',
          borderStyle: 'solid',
          ...PaletteBorderStyle,
        }}
      >
        {headless || (
          <Row
            data-drag-handle
            style={{
              height: PaletteMetrics.headerHeight,
              alignContent: 'center',
              paddingBlockStart: 0,
              backgroundColor: '#FFFFFF',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {leadingAction}
            </div>
            <div
              style={{
                padding: PaletteMetrics.padding,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Font kind="font-palette-header">{title}</Font>
            </div>
            <Spacer />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {trailingAction}
            </div>
          </Row>
        )}
        <Scrollable
          style={{ padding: PaletteMetrics.padding, cursor: 'auto', maxHeight }}
        >
          {children}
        </Scrollable>
        {footerAction && (
          <Row
            style={{
              justifyContent: 'flex-end',
              marginInlineEnd: 8,
              marginBlockEnd: 8,
            }}
          >
            {footerAction}
          </Row>
        )}
      </Column>
    </Draggable>
  );
}

export type Position = {
  x: number;
  y: number;
};

export type DraggableProps = {
  windowID: WindowID;
};

export function Draggable({
  windowID,
  children,
}: React.PropsWithChildren<DraggableProps>) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { state: windowState, dispatch: windowDispatch } = useWindows();

  type DragState = {
    offset: Position;
  };

  const _initialPosition = windowState.windows[windowID]?.position;

  const hasResolvedPosition =
    _initialPosition !== undefined && _initialPosition.type === 'absolute';
  const initialResolvedPosition: Position = hasResolvedPosition
    ? { x: _initialPosition.x, y: _initialPosition.y }
    : (resolvedPosition(windowID, windowState) ?? { x: 0, y: 0 });
  const [position, _setPosition] = useState<Position>({
    x: initialResolvedPosition.x,
    y: initialResolvedPosition.y,
  });

  const width = windowState.windows[windowID]?.size?.width;
  const height = windowState.windows[windowID]?.size?.height;
  function setPosition(position: Position) {
    _setPosition({ x: position.x, y: position.y });
    windowDispatch({
      type: 'setPosition',
      id: windowID,
      position: { type: 'absolute', x: position.x, y: position.y },
    });
  }

  const [dragState, _setDragState] = useState<DragState | null>(null);
  const setDragState = useCallback(
    (state: DragState | null) => {
      const isFixed = windowState.windows[windowID]?.options?.fixed === true;
      if (isFixed) {
        return;
      }
      _setDragState(state);
    },
    [windowID, windowState],
  );

  function isLeftClick(e: React.MouseEvent) {
    return e.button === 0;
  }

  function isTargetHTMLElement(e: React.MouseEvent) {
    return e.target instanceof HTMLElement;
  }

  const onMouseDown = (e: React.MouseEvent) => {
    if (!isLeftClick(e) || !isTargetHTMLElement(e)) {
      return;
    }

    const target = e.target as HTMLElement;
    const interactiveElement = target.closest(
      'input, textarea, select, option, button, a, [contenteditable="true"]',
    );
    if (interactiveElement !== null) {
      return;
    }

    const container = containerRef.current;
    const handleElement = target.closest('[data-drag-handle]');
    const hasDragHandle =
      container?.querySelector('[data-drag-handle]') !== null;

    if (hasDragHandle) {
      const clickedHandle =
        handleElement !== null && container?.contains(handleElement);
      if (!clickedHandle) {
        return;
      }
    }

    setDragState({
      offset: {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      },
    });
  };

  useEffect(() => {
    if (dragState === null) {
      return;
    }

    const onMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragState.offset.x,
        y: e.clientY - dragState.offset.y,
      });
    };

    const onMouseUp = (_: MouseEvent) => {
      setDragState(null);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragState, setDragState, setPosition]);

  useEffect(
    function setPositionIfChanged() {
      if (dragState !== null) {
        return;
      }

      const resolved = resolvedPosition(windowID, windowState);
      if (resolved === null) {
        // We couldn't resolve the position yet - it's possible we depend on another windowID, nothing to do.
        return;
      }

      const hasChanged = position.x !== resolved.x || position.y !== resolved.y;
      if (hasChanged) {
        _setPosition({ x: resolved.x, y: resolved.y });
      }
    },
    [dragState, windowID, windowState, position],
  );

  const window = windowState.windows[windowID];
  const cursor: string = useMemo(() => {
    if (window?.options?.fixed) {
      return 'auto';
    }

    return dragState === null ? 'grab' : 'grabbing';
  }, [window, dragState]);
  const isHidden = windowState.windows[windowID]?.isHidden === true;
  if (isHidden) {
    return <></>;
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: width !== null ? width : 'auto',
        height: height !== undefined ? height : 'auto',
        display: 'flex',
        flexDirection: 'column',
        cursor,
        position: 'absolute',
        left: position.x,
        top: position.y,
        // Ensure palettes render above CleanSlate overlay (which has no z-index)
        zIndex: 10,
      }}
      onMouseDown={onMouseDown}
    >
      {children}
    </div>
  );
}
